import React, { FC, useState, useMemo, useEffect } from 'react';
import { orderBy } from 'lodash';
import { useHistory } from 'react-router-dom';
import { AuthWrapper } from '@src/components/AuthWrapper';
import { Appearance, Button } from '@src/components/Button';
import { useCompanySizesQuery, useGetCountriesQuery, useSignUpMutation } from '@src/graphql/generated';
import { Endpoint } from '@src/config/enums/endpoints.enum';
import { useAppDispatch } from '@src/redux';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { fetchAccessToken } from '@src/redux/actions/accessToken';
import { FEATURED_APPLICANTS_ROUTE } from '@src/Routes/links';
import { trackEvent } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { StepOne } from './StepOne';
import { FormKey } from './enums/formKey.enum';
import { FormData } from './interfaces/formData.interface';
import { StepTwo } from './StepTwo';
import { validateRegisterFormFirstStep, validateRegisterFormSecondStep } from './utils/validateRegisterForm';
import { getInitialFormState } from './utils/getInitialFormState';
import './styles.scss';

export const Register: FC = () => {
  const dispatch = useAppDispatch();

  const history = useHistory();

  const initialData = useMemo(() => getInitialFormState(), []);

  const [activeStep, setActiveStep] = useState<number>(1);

  const [formData, setFormData] = useState<FormData>(initialData);

  const [signUp, { loading: isSignUpLoading }] = useSignUpMutation({
    onCompleted: async () => {
      dispatch(setSuccessFlashMessage('Account registered successfully'));

      await dispatch(fetchAccessToken());

      history.replace({ pathname: FEATURED_APPLICANTS_ROUTE });
    },
    onError: error => {
      dispatch(setErrorFlashMessage(`Could not manage to sign up user. ${error?.graphQLErrors?.[0]?.message}`));
    },
  });

  const { data: companyData, loading: isCompanySizesLoading } = useCompanySizesQuery({
    context: {
      clientName: Endpoint.PUBLIC,
    },
    fetchPolicy: 'network-only',
  });
  const { data: countriesData, loading: isCountriesLoading } = useGetCountriesQuery({
    context: {
      clientName: Endpoint.PUBLIC,
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    trackEvent({
      context: MetricContext.REGISTER_PAGE,
      interaction: MetricAction.VIEW_REGISTER_PAGE,
    });
  }, []);

  const isLoading = isCompanySizesLoading || isCountriesLoading;

  const companySizes = companyData?.companySizes;
  const countries = orderBy(
    countriesData?.countries.result.map(country => ({ ...country, name: country.longName })),
    'longName',
    'asc',
  );

  const onChange = (key: FormKey, value: string) => {
    setFormData({
      ...formData,
      [key]: { error: undefined, key: key, value: value },
    });
  };

  const renderActiveStep = (activeStep: number): React.ReactNode | null => {
    switch (activeStep) {
      case 1:
        return <StepOne formData={formData} onChange={onChange} />;
      case 2:
        if (isLoading || !companySizes) {
          return (
            <div className="register__skeleton-wrapper pulse-fade">
              <div className="register__skeleton-field" />
              <div className="register__skeleton-field" />
              <div className="register__skeleton-field" />
              <div className="register__skeleton-field" />
              <div className="register__skeleton-field" />
              <div className="register__skeleton-field" />
            </div>
          );
        }

        return <StepTwo companySizes={companySizes} countries={countries} formData={formData} onChange={onChange} />;
      default:
        return null;
    }
  };

  const signUpUser = () => {
    trackEvent({
      context: MetricContext.REGISTER_PAGE,
      interaction: MetricAction.SIGN_UP_USER,
      payload: { email: formData.workEmail.value },
    });

    signUp({
      variables: {
        signUpInput: {
          companyName: formData.companyName.value,
          companySize: formData.companySize.value,
          country: formData.country.value,
          email: formData.workEmail.value,
          fullName: formData.fullName.value,
          googleRecaptchaResponse: formData.captcha.value,
          password: formData.password.value,
          phoneNumber: formData.phoneNumber.value,
          positionTitle: formData.positionTitle.value,
        },
      },
    });
  };

  const onContinue = () => {
    if (activeStep === 1) {
      const validateFirstStepData = validateRegisterFormFirstStep(formData);
      const isFirstStepFormValid = !Object.values(validateFirstStepData).find(it => it.error);

      if (isFirstStepFormValid) {
        setActiveStep(activeStep + 1);

        trackEvent({
          context: MetricContext.REGISTER_PAGE,
          interaction: MetricAction.OPEN_REGISTER_PAGE_SECOND_STEP,
        });
      } else {
        setFormData(validateFirstStepData);
      }
    } else if (activeStep === 2) {
      const validateSecondStepData = validateRegisterFormSecondStep(formData);
      const isSecondStepFormValid = !Object.values(validateSecondStepData).find(it => it.error);

      if (isSecondStepFormValid) {
        signUpUser();

        trackEvent({
          context: MetricContext.REGISTER_PAGE,
          interaction: MetricAction.FINISH_RECRUITER_SIGNUP,
        });
      } else {
        setFormData(validateSecondStepData);
      }
    }
  };

  const onBack = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <AuthWrapper wrapperClassName="register">
      {renderActiveStep(activeStep)}

      <div className="register__action-buttons">
        <Button className="register__continue-button" isDisabled={isSignUpLoading} onClick={onContinue}>
          {activeStep === 1 ? 'Continue' : 'Register'}
        </Button>

        {activeStep > 1 && (
          <Button
            appearance={Appearance.SECONDARY}
            className="register__back-button"
            isDisabled={activeStep === 1}
            onClick={onBack}
          >
            Back
          </Button>
        )}
      </div>
    </AuthWrapper>
  );
};
